<template>
    <v-form ref="form" v-model="valid" data-cy="department-form">
        <v-row dense>
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="10" class="align-center">
                <v-row>
                    <v-switch
                        v-model="department.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="department.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-col cols="12" sm="6" md="4" class="px-0">
            <v-text-field
                v-model="department.name[activeLangCode]"
                variant="outlined"
                density="compact"
                color="primary"
                data-cy="name"
                :label="`${$t('name')}*`"
                :rules="[requiredRule('name')]"
            >
                <template v-slot:append-inner>
                    <span v-if="activeLangCode" class="fi" :class="[`fi-${useFlagIconCode(activeLangCode)}`]" />
                </template>
            </v-text-field>
        </v-col>
        <v-checkbox
            v-model="department.legalClient"
            :label="$t('department_is_client')"
            density="compact"
            color="primary"
            data-cy="is-legal-client-department"
        />
        <div v-if="department.legalClient">
            <h4 class="text-h6 text-capitalize pb-2">
                {{ $t('additional_information') }}
            </h4>
            <v-col cols="12" md="8" class="px-0">
                <v-row dense>
                    <v-col cols="12" sm="6" md="6" order-md="1">
                        <v-text-field
                            v-model="department.fullLegalName[activeLangCode]"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="full-legal-name"
                            :label="$t('full_legal_name')"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="3">
                        <v-text-field
                            v-model="department.address[activeLangCode]"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="address"
                            :label="$t('address')"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="5">
                        <v-text-field
                            v-model="department.legalAddress[activeLangCode]"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="legal-address"
                            :label="$t('legal_address')"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="2">
                        <v-text-field
                            v-model="department.codeITN"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="ITN"
                            label="ITN"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="4">
                        <v-text-field
                            v-model="department.codeKPP"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="KPP"
                            label="KPP"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <h4 class="text-h6 text-capitalize pb-2">
                {{ $t('bank_details') }}
            </h4>
            <v-col cols="12" md="8" class="px-0">
                <v-row dense>
                    <v-col cols="12" sm="6" md="6" order-md="1">
                        <v-text-field
                            v-model="department.currentAccount"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="current-account"
                            :label="$t('current_account')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="3">
                        <v-text-field
                            v-model="department.bankName[activeLangCode]"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="bank-name"
                            :label="$t('bank_name')"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="5">
                        <v-text-field
                            v-model="department.bankIdentificationCode"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="bank-identification-code"
                            :label="$t('bank_identification_code')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="7">
                        <v-text-field
                            v-model="department.correspondentAccount"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="correspondent-account"
                            :label="$t('correspondent_account')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="2">
                        <v-text-field
                            v-model="department.codeOKPO"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="OKPO"
                            :label="$t('OKPO')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="4">
                        <v-text-field
                            v-model="department.codeOKONKH"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="OKONKh"
                            :label="$t('OKONKh')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="6" order-md="6">
                        <v-text-field
                            v-model="department.ceoName[activeLangCode]"
                            variant="outlined"
                            density="compact"
                            color="primary"
                            data-cy="CEO"
                            :label="$t('CEO')"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-col>
        </div>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import type {DepartmentType} from '~/utils/types/account/departments/types'
    import {initDepartment} from '~/utils/types/account/departments/helper'
    import {clean} from '~/utils/helpers'

    const props = defineProps<{
        departmentId?: number | null
        companyId: number
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const department = ref<DepartmentType>(initDepartment())

    onMounted(async () => {
        if (props.departmentId) await loadDepartment()
    })

    async function loadDepartment(): Promise<void> {
        try {
            isLoading.value = true
            const {departments} = await app.$api.departments.get({id: props.departmentId, locale: 'all'})
            department.value = Object.assign(initDepartment(), departments[0])
        } catch (e) {
            department.value = initDepartment()
        } finally {
            isLoading.value = false
        }
    }

    const useLocales = computed(() => {
        const locales = app.$conf.public.locales
        return locales.map(lang => {
            return {value: lang, title: t(`languages.${lang}`), icon: useFlagIconCode.value(lang)}
        })
    })

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(department.value)
            clean(data)
            if (props.departmentId) {
                delete data.companyId
                await app.$api.departments.put(props.departmentId, data)
            } else {
                await app.$api.departments.post({...data, companyId: props.companyId})
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
